import React, { Dispatch, useMemo, useState } from 'react'
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
} from '@mui/material'
import { addMonths, format, getUnixTime } from 'date-fns'
import { Sprint } from '@marketing-milk/interfaces'
import { useNest } from 'hooks/api/useNest'
import { useSprintPackets } from 'hooks/api/useSprintPackets'
import { CreativeImportAction, CreativeImportState } from '../state'

interface Props {
  state: CreativeImportState
  dispatch: Dispatch<CreativeImportAction>
  onContinue: () => void
}

/**
 * Sentinel value for the "create a new sprint" item at the bottom of the sprint
 * dropdown. Selecting it reveals the inline create form; there is deliberately
 * no existing-vs-new mode toggle, so the AE only ever answers one question
 * ("which sprint?") and the step keeps a single commit button.
 */
const NEW_SPRINT = 'new'

/** yyyy-MM-dd for a native date input — the inline form is deliberately plain. */
const asDateInput = (date: Date) => format(date, 'yyyy-MM-dd')

/**
 * Noon, matching the sprint wizard: a sprint boundary is a day, and midnight
 * local can flip across a timezone into the neighbouring day.
 */
const dateInputToUnix = (value: string) => getUnixTime(new Date(`${value}T12:00:00`))

/**
 * Most-recent-first. Imports arrive in batches against one sprint, so the sprint
 * an AE most recently created is nearly always the one batch two belongs to —
 * and `GET /sprints` returns no ordering of its own.
 */
export function sortSprintsByNewest(sprints: Sprint[]): Sprint[] {
  return [...sprints].sort((a, b) => b.createdTime - a.createdTime)
}

export const OptionsStep = ({ state, dispatch, onContinue }: Props) => {
  const {
    list: sprints,
    isLoading,
    create: createSprint,
    refetch: refetchSprints,
  } = useNest('sprints')
  const { list: packets } = useSprintPackets()
  const [attachEnabled, setAttachEnabled] = useState(state.sprintID !== null)
  const [creatingNew, setCreatingNew] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newSprint, setNewSprint] = useState(() => {
    const start = new Date()
    const end = addMonths(start, 3)
    return {
      // Same Mon-Mon convention the sprint wizard defaults to.
      name: `${format(start, 'MMM')}-${format(end, 'MMM')}`,
      startTime: asDateInput(start),
      endTime: asDateInput(end),
      budget: '',
    }
  })

  const orderedSprints = useMemo(() => sortSprintsByNewest(sprints ?? []), [sprints])
  const selectedCount = Object.values(state.rows).filter(row => row.selected).length
  const cannotContinue = selectedCount === 0 || (attachEnabled && state.sprintID === null)

  const existingPacket = packets?.find(packet => packet.sprintID === state.sprintID)

  const newSprintValid =
    newSprint.name.trim().length > 0 &&
    newSprint.startTime !== '' &&
    newSprint.endTime !== '' &&
    Number(newSprint.budget) > 0 &&
    dateInputToUnix(newSprint.endTime) > dateInputToUnix(newSprint.startTime)

  const handleAttachChange = (checked: boolean) => {
    setAttachEnabled(checked)
    const sprintID = checked ? orderedSprints[0]?.id ?? null : null
    // A business with no sprints yet has nothing to pick, so open the create
    // form rather than showing an empty dropdown.
    setCreatingNew(checked && sprintID === null)
    dispatch({ type: 'setSprint', sprintID })
  }

  const handleSprintChange = (value: string) => {
    if (value === NEW_SPRINT) {
      setCreatingNew(true)
      dispatch({ type: 'setSprint', sprintID: null })
      return
    }
    setCreatingNew(false)
    dispatch({ type: 'setSprint', sprintID: Number(value) })
  }

  const handleCreateSprint = async () => {
    setCreating(true)
    try {
      const created = await createSprint({
        name: newSprint.name.trim(),
        startTime: dateInputToUnix(newSprint.startTime),
        endTime: dateInputToUnix(newSprint.endTime),
        budget: Number(newSprint.budget),
      })
      // useNest raises its own error toast and resolves undefined on failure.
      if (!created) return
      await refetchSprints()
      dispatch({ type: 'setSprint', sprintID: created.id })
      // Collapse the form — from here on it is a real sprint and reads in the
      // dropdown exactly like any other choice.
      setCreatingNew(false)
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-gray-700">
        {selectedCount} creative{selectedCount === 1 ? '' : 's'} will be imported as drafts.
      </p>

      <FormControlLabel
        control={
          <Switch
            checked={attachEnabled}
            disabled={isLoading}
            onChange={e => handleAttachChange(e.target.checked)}
          />
        }
        label="Add these creatives to a sprint"
      />

      {attachEnabled && (
        <div className="flex flex-col gap-4 pl-2">
          <TextField
            select
            size="small"
            label="Sprint"
            value={creatingNew ? NEW_SPRINT : state.sprintID ?? ''}
            disabled={isLoading}
            onChange={e => handleSprintChange(e.target.value)}
            className="max-w-sm"
            helperText="Most recent first — pick the sprint you're filling in batches."
          >
            {orderedSprints.map(sprint => (
              <MenuItem key={sprint.id} value={sprint.id}>
                {sprint.name}
              </MenuItem>
            ))}
            {orderedSprints.length > 0 && <Divider />}
            <MenuItem value={NEW_SPRINT}>+ Create a new sprint…</MenuItem>
          </TextField>

          {creatingNew && (
            <div className="flex flex-col gap-3 max-w-sm">
              <TextField
                size="small"
                label="Sprint name"
                value={newSprint.name}
                onChange={e => setNewSprint(prev => ({ ...prev, name: e.target.value }))}
              />
              <div className="flex gap-3">
                <TextField
                  size="small"
                  type="date"
                  label="Starts"
                  InputLabelProps={{ shrink: true }}
                  value={newSprint.startTime}
                  onChange={e => setNewSprint(prev => ({ ...prev, startTime: e.target.value }))}
                />
                <TextField
                  size="small"
                  type="date"
                  label="Ends"
                  InputLabelProps={{ shrink: true }}
                  value={newSprint.endTime}
                  onChange={e => setNewSprint(prev => ({ ...prev, endTime: e.target.value }))}
                />
              </div>
              <TextField
                size="small"
                type="number"
                label="Budget"
                value={newSprint.budget}
                onChange={e => setNewSprint(prev => ({ ...prev, budget: e.target.value }))}
              />
              <Button
                variant="outlined"
                className="self-start"
                disabled={!newSprintValid || creating}
                onClick={() => void handleCreateSprint()}
              >
                {creating ? 'Creating…' : 'Create sprint'}
              </Button>
            </div>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(existingPacket) || state.createPacket}
                disabled={Boolean(existingPacket)}
                onChange={e =>
                  dispatch({ type: 'setCreatePacket', createPacket: e.target.checked })
                }
              />
            }
            label={
              existingPacket
                ? 'This sprint already has a client packet; imported creatives will appear there'
                : 'Create a client packet for this sprint'
            }
          />
        </div>
      )}

      <div className="flex justify-between">
        <Button onClick={() => dispatch({ type: 'backToReview' })}>Back</Button>
        <Button variant="contained" onClick={onContinue} disabled={cannotContinue}>
          Continue
        </Button>
      </div>
    </div>
  )
}
